import { t } from 'i18next';

import {
  type FinancialStatementRawItem,
  getAssetOverviewFinancialsUrl,
  getStatementDisplayLabel,
  normalizeFinancialStatementItems,
} from '../financial-statements-item/financialStatementUtils';
import type { ConversationInventory } from '../services/inventory-service';
import { htmlNodes } from './htmlNodes';

export type InventoryContent = { html: string; text: string };

export const buildInventoryContent = (
  inventory?: ConversationInventory,
  baseUrl?: string,
  rawFinancialStatementItems?: FinancialStatementRawItem[]
): InventoryContent => {
  let html = '';
  let text = '';

  if (!inventory || Object.keys(inventory).length === 0) {
    return { html, text };
  }

  const entities = inventory?.entity?.items?.map(i => i.tag) ?? [];
  const timeseries = inventory?.timeseries?.items?.map(i => i.snake) ?? [];
  const webLinks =
    inventory?.['web-links']?.items?.flatMap(i => i.urls ?? []) ?? [];
  const financialStatementItems = normalizeFinancialStatementItems(
    inventory?.['financial-statement']?.items ?? []
  );
  const rawItemsForLinks = normalizeFinancialStatementItems(
    rawFinancialStatementItems ??
      inventory?.['financial-statement']?.items ??
      []
  );
  const financialStatementsText = financialStatementItems.map(item =>
    item.statement
      ? `${item.entityTag} - ${getStatementDisplayLabel(item.statement)}`
      : item.entityTag
  );
  const financialStatementsHtml = baseUrl
    ? financialStatementItems.map((item, i) => {
        const rawItem = rawItemsForLinks[i];
        const entityTag = rawItem?.entityTag ?? item.entityTag;
        const displayText = item.statement
          ? `${item.entityTag} - ${getStatementDisplayLabel(item.statement)}`
          : item.entityTag;
        return htmlNodes.a(
          displayText,
          getAssetOverviewFinancialsUrl(entityTag, baseUrl, item.statement)
        );
      })
    : financialStatementsText;
  const externalData =
    inventory?.['external-data']?.items?.flatMap(i => i.links ?? []) ?? [];

  const sections = [
    {
      title: t('chat:inventory.sections.entities', { count: entities.length }),
      textItems: entities,
      htmlItems: entities,
    },
    {
      title: t('chat:inventory.sections.timeSeries', {
        count: timeseries.length,
      }),
      textItems: timeseries,
      htmlItems: timeseries,
    },
    {
      title: t('chat:inventory.sections.financials', {
        count: financialStatementItems.length,
      }),
      textItems: financialStatementsText,
      htmlItems: financialStatementsHtml,
    },
    {
      title: t('chat:inventory.sections.externalData', {
        count: externalData.length,
      }),
      textItems: externalData,
      htmlItems: externalData
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
    {
      title: t('chat:inventory.sections.webLinks', { count: webLinks.length }),
      textItems: webLinks,
      htmlItems: webLinks
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
  ];

  const nonEmpty = sections.filter(s => s.textItems.length > 0);

  if (!nonEmpty.length) {
    return { html, text };
  }

  text += `\n\n## ${t('chat:inventory.sources')}`;
  nonEmpty.forEach(s => {
    text += `\n\n### ${s.title}\n` + s.textItems.map(v => `- ${v}`).join('\n');
  });

  html += htmlNodes.h2(t('chat:inventory.sources'));
  nonEmpty.forEach(s => {
    html += htmlNodes.h3(s.title);
    html += htmlNodes.unorderedList(
      s.htmlItems
        .filter(v => v !== null && v !== undefined)
        .map(v => htmlNodes.unorderedListItem(v))
        .join('')
    );
  });

  return { html, text };
};
